import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  ArrowLeft, Save, FileText, Percent, DollarSign, Calendar as CalendarIcon, 
  Settings, Shield, Image as ImageIcon, Flag, ChevronDown, ChevronRight,
  Plus, Trash2, Upload, X, HelpCircle, Bold, Italic, List, ListOrdered, Heading2, Eye
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Numeric input with suffix — accepts both comma and dot
function NumericInput({ value, onChange, suffix, placeholder }) {
  const inputRef = useRef(null);
  const [localVal, setLocalVal] = useState(String(value ?? '0'));
  const isFocused = useRef(false);

  // Only sync from parent when not editing
  if (!isFocused.current && String(value) !== localVal) {
    // Use a direct assignment instead of setState to avoid render loops
  }

  return (
    <div className="flex items-center border rounded-md border-input bg-background overflow-hidden focus-within:ring-1 focus-within:ring-ring">
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        defaultValue={value ?? '0'}
        onFocus={(e) => {
          isFocused.current = true;
          // Select all on focus for easy overwrite
          e.target.select();
        }}
        onChange={(e) => {
          const raw = e.target.value;
          // Allow empty, digits, one dot or comma
          if (raw !== '' && !/^-?\d*[.,]?\d*$/.test(raw)) {
            e.preventDefault();
            return;
          }
          // Don't call onChange during typing — wait for blur
        }}
        onBlur={(e) => {
          isFocused.current = false;
          const raw = e.target.value.replace(',', '.');
          const parsed = parseFloat(raw);
          if (isNaN(parsed) || raw.trim() === '') {
            e.target.value = '0';
            onChange(0);
          } else {
            e.target.value = String(parsed);
            onChange(parsed);
          }
        }}
        placeholder={placeholder || '0'}
        className="flex-1 h-9 px-3 bg-transparent text-sm outline-none"
      />
      {suffix && <span className="text-xs text-muted-foreground pr-3 select-none">{suffix}</span>}
    </div>
  );
}

// Simple rich text editor using contentEditable
const RichEditor = React.memo(function RichEditor({ value, onChange, placeholder, rows = 6 }) {
  const editorRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(!value);
  const isEditingRef = useRef(false);
  const lastSetRef = useRef('');

  // Set content when value changes externally (e.g. product loaded from API)
  useEffect(() => {
    if (!editorRef.current) return;
    if (isEditingRef.current) return;
    // Only update if value actually differs from what we last set
    if (value !== lastSetRef.current) {
      editorRef.current.innerHTML = value || '';
      lastSetRef.current = value || '';
      setIsEmpty(!value);
    }
  }, [value]);

  const handleInput = () => {
    isEditingRef.current = true;
    const html = editorRef.current?.innerHTML || '';
    const textOnly = editorRef.current?.textContent || '';
    setIsEmpty(!textOnly.trim());
    lastSetRef.current = html;
    onChange(html);
    setTimeout(() => { isEditingRef.current = false; }, 100);
  };

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    handleInput();
  };

  return (
    <div className="border rounded-md border-input overflow-hidden focus-within:ring-1 focus-within:ring-ring">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border/50 bg-muted/30">
        <button type="button" onClick={() => exec('bold')} className="p-1.5 rounded hover:bg-muted transition-colors" title="Gras"><Bold className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => exec('italic')} className="p-1.5 rounded hover:bg-muted transition-colors" title="Italique"><Italic className="w-3.5 h-3.5" /></button>
        <div className="w-px h-4 bg-border/50 mx-1" />
        <button type="button" onClick={() => exec('formatBlock', 'h3')} className="p-1.5 rounded hover:bg-muted transition-colors" title="Sous-titre"><Heading2 className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => exec('insertUnorderedList')} className="p-1.5 rounded hover:bg-muted transition-colors" title="Liste"><List className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => exec('insertOrderedList')} className="p-1.5 rounded hover:bg-muted transition-colors" title="Liste numerotee"><ListOrdered className="w-3.5 h-3.5" /></button>
        <div className="w-px h-4 bg-border/50 mx-1" />
        <button type="button" onClick={() => exec('formatBlock', 'p')} className="px-2 py-1 rounded hover:bg-muted transition-colors text-xs text-muted-foreground" title="Paragraphe">P</button>
      </div>
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={handleInput}
          className="px-3 py-2 text-sm outline-none min-h-[150px] prose prose-sm dark:prose-invert max-w-none [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:my-1.5 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5"
          style={{ minHeight: `${rows * 24}px` }}
          data-testid="rich-editor"
        />
        {isEmpty && (
          <div className="absolute top-2 left-3 text-sm text-muted-foreground/50 pointer-events-none">
            {placeholder || 'Commencez a ecrire...'}
          </div>
        )}
      </div>
    </div>
  );
});

export default function AdminPlacementForm() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEditing = !!productId;
  
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tiersOpen, setTiersOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    rentability: true,
    pricing: true,
    product: true,
    images: true,
    contract: true,
    options: true,
    banner: true
  });
  
  // Wrap setProduct to preserve scroll position on state change
  const [productState, setProductState] = useState({
    name: '',
    slug: '',
    category_id: '',
    subcategory: '',
    description: '',
    terms_conditions: '',
    is_active: true,
    // Rentability
    has_rentability: true,
    duration_months: 12,
    rentability_rate: 0,
    rentability_variable: 0,
    rentability_period: 'Annuel',
    show_min_rentability: false,
    interest_periods: {
      monthly: false,
      quarterly: false,
      biannual: false,
      annual: true,
      end_of_contract: false
    },
    interest_compound: false,
    interest_payout_target: 'available',
    capitalize_funds: false,
    // Pricing
    base_price: 0,
    min_investment: 0,
    max_investment: 0,
    variable_price: false,
    price_variations: [],
    percentage_by_amount: [],
    // Product management
    show_at_launch: true,
    launch_date: '',
    end_date: '',
    show_reference: true,
    enable_offers: false,
    is_watch: false,
    is_savings: false,
    linked_to_positions: false,
    funds_locked: false,
    custom_data_file: null,
    // Images
    images: [],
    // Contract
    brochure_url: '',
    enable_platform_contract: true,
    enable_custom_contract: false,
    contract_version: 'v1',
    auto_renewal: false,
    contract_guarantee: false,
    confidence_index: 100,
    // Options
    is_crowdfunding: false,
    manage_stock: false,
    is_unique: false,
    // Banner
    banner_country: '',
    banner_text: '',
    banner_color: '#22c55e',
    order: 0
  });

  const scrollYRef = useRef(0);
  const setProduct = useCallback((updater) => {
    scrollYRef.current = window.scrollY;
    setProductState(updater);
  }, []);
  const product = productState;

  // Restore scroll position after every render caused by setProduct
  useEffect(() => {
    if (scrollYRef.current > 0) {
      window.scrollTo(0, scrollYRef.current);
    }
  });

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchProduct();
    }
  }, [productId]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/placements/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/placements/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProduct(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      toast.error('Produit non trouvé');
      navigate('/admin/placements');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!product.name || !product.category_id) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    try {
      setLoading(true);
      if (isEditing) {
        await axios.put(`${API}/placements/products/${productId}`, product, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API}/placements/products`, product, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produit créé');
      }
      navigate('/admin/placements');
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur lors de la sauvegarde' });
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const addImage = () => {
    setProduct(prev => ({ ...prev, images: [...prev.images, ''] }));
  };

  const updateImage = (index, value) => {
    setProduct(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? value : img)
    }));
  };

  const removeImage = (index) => {
    setProduct(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const [uploadingImage, setUploadingImage] = useState(null);
  const handleImageUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingImage(index);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      updateImage(index, fullUrl);
      toast.success('Image uploadee');
    } catch (err) {
      toast.error('Erreur upload', { description: err.response?.data?.detail || 'Echec upload' });
    } finally {
      setUploadingImage(null);
    }
  };

  const handleNewImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingImage(-1);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setProduct(prev => ({ ...prev, images: [...prev.images, fullUrl] }));
      toast.success('Image uploadee');
    } catch (err) {
      toast.error('Erreur upload', { description: err.response?.data?.detail || 'Echec upload' });
    } finally {
      setUploadingImage(null);
    }
  };

  const SectionHeader = ({ icon: Icon, title, section, children }) => (
    <Collapsible open={expandedSections[section]} onOpenChange={() => toggleSection(section)}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-muted/30 rounded-t-lg border border-border/50 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-blue-500" />
          <span className="font-semibold">{title}</span>
        </div>
        {expandedSections[section] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-4 border border-t-0 border-border/50 rounded-b-lg space-y-4">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );

  const selectedCategory = categories.find(c => c.id === product.category_id);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/placements')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isEditing ? product.name || 'Modifier le produit' : 'Créer un produit'}
              </h1>
              {isEditing && product.reference && (
                <p className="text-sm text-muted-foreground">Réf: {product.reference}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowGuide(true)} data-testid="show-guide-btn">
              <HelpCircle className="w-4 h-4 mr-2" />
              Guide de paramétrage
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>

        {/* Informations de base */}
        <Card className="bg-card/80 backdrop-blur border-border/50">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              <CardTitle>Informations de base</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 pb-4 border-b border-border/50">
              <Switch 
                checked={product.is_active} 
                onCheckedChange={(v) => setProduct(prev => ({ ...prev, is_active: v }))}
              />
              <Label>Actif</Label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom du produit *</Label>
                <Input 
                  value={product.name} 
                  onChange={(e) => setProduct(prev => ({ 
                    ...prev, 
                    name: e.target.value,
                    slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
                  }))}
                  placeholder="Ex: CryptoBoost Épargne+"
                />
              </div>
              <div className="space-y-2">
                <Label>URL formaté</Label>
                <Input 
                  value={product.slug} 
                  onChange={(e) => setProduct(prev => ({ ...prev, slug: e.target.value }))}
                  className="bg-muted/30"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Catégorie *</Label>
                <Select 
                  value={product.category_id || 'none'} 
                  onValueChange={(v) => setProduct(prev => ({ ...prev, category_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Sélectionner --</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sous-catégorie</Label>
                {selectedCategory?.subcategories?.length > 0 ? (
                  <Select 
                    value={product.subcategory || 'none'} 
                    onValueChange={(v) => setProduct(prev => ({ ...prev, subcategory: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Aucune --</SelectItem>
                      {selectedCategory.subcategories.map(sub => (
                        <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input 
                    value={product.subcategory || ''} 
                    onChange={(e) => setProduct(prev => ({ ...prev, subcategory: e.target.value }))}
                    placeholder="Sous-catégorie"
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <RichEditor 
                value={product.description || ''} 
                onChange={(html) => setProduct(prev => ({ ...prev, description: html }))}
                rows={6}
                placeholder="Decrivez le produit : caracteristiques, avantages, conditions..."
              />
              <p className="text-xs text-muted-foreground">Utilisez la barre d'outils pour mettre en forme (gras, italique, listes, sous-titres)</p>
            </div>
          </CardContent>
        </Card>

        {/* Gestion de la rentabilité */}
        <SectionHeader icon={Percent} title="Gestion de la rentabilité" section="rentability">
          <div className="flex items-center gap-2 pb-4 border-b border-border/50">
            <Checkbox 
              checked={!product.has_rentability}
              onCheckedChange={(v) => setProduct(prev => ({ ...prev, has_rentability: !v }))}
            />
            <Label>Produit sans rentabilité</Label>
          </div>
          
          {product.has_rentability && (
            <>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Duree (mois)</Label>
                  <NumericInput 
                    value={product.duration_months} 
                    onChange={(v) => setProduct(prev => ({ ...prev, duration_months: Math.round(v) }))}
                    suffix="mois"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rentabilite (%)</Label>
                  <NumericInput 
                    value={product.rentability_rate} 
                    onChange={(v) => setProduct(prev => ({ ...prev, rentability_rate: v }))}
                    suffix="%"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rentabilite variable (%)</Label>
                  <NumericInput 
                    value={product.rentability_variable} 
                    onChange={(v) => setProduct(prev => ({ ...prev, rentability_variable: v }))}
                    suffix="%"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Période de rentabilité</Label>
                  <Select 
                    value={product.rentability_period} 
                    onValueChange={(v) => setProduct(prev => ({ ...prev, rentability_period: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mensuel">Mensuel</SelectItem>
                      <SelectItem value="Trimestriel">Trimestriel</SelectItem>
                      <SelectItem value="Semestriel">Semestriel</SelectItem>
                      <SelectItem value="Annuel">Annuel</SelectItem>
                      <SelectItem value="Fin de contrat">Fin de contrat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={product.show_min_rentability}
                  onCheckedChange={(v) => setProduct(prev => ({ ...prev, show_min_rentability: v }))}
                />
                <Label>Afficher rentabilité minimum</Label>
              </div>

              <div className="space-y-2">
                <Label>Période d'intérêt disponible</Label>
                <div className="flex flex-wrap gap-4">
                  {[
                    { key: 'monthly', label: 'Mensuel' },
                    { key: 'quarterly', label: 'Trimestriel' },
                    { key: 'biannual', label: 'Semestriel' },
                    { key: 'annual', label: 'Annuel' },
                    { key: 'end_of_contract', label: 'Fin de contrat' }
                  ].map(period => (
                    <div key={period.key} className="flex items-center gap-2">
                      <Checkbox 
                        checked={product.interest_periods?.[period.key] || false}
                        onCheckedChange={(v) => setProduct(prev => ({ 
                          ...prev, 
                          interest_periods: { ...prev.interest_periods, [period.key]: v }
                        }))}
                        data-testid={`interest-period-${period.key}-checkbox`}
                      />
                      <Label className="font-normal">{period.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={product.interest_compound}
                    onCheckedChange={(v) => setProduct(prev => ({ ...prev, interest_compound: v }))}
                    data-testid="interest-compound-checkbox"
                  />
                  <Label>Intérêts composés (cumulables)</Label>
                </div>
                <div className="space-y-2 min-w-[220px]">
                  <Label>Versement des intérêts</Label>
                  <Select
                    value={product.interest_payout_target}
                    onValueChange={(v) => setProduct(prev => ({ ...prev, interest_payout_target: v }))}
                  >
                    <SelectTrigger data-testid="interest-payout-target-select">
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available" data-testid="interest-payout-available">Fonds disponibles</SelectItem>
                      <SelectItem value="livret" data-testid="interest-payout-livret">Capital du livret</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={product.capitalize_funds}
                    onCheckedChange={(v) => setProduct(prev => ({ ...prev, capitalize_funds: v }))}
                    data-testid="capitalize-funds-checkbox"
                  />
                  <Label>Capitalisation des fonds</Label>
                </div>
              </div>
            </>
          )}
        </SectionHeader>

        {/* Gestion des prix */}
        <SectionHeader icon={DollarSign} title="Gestion des prix" section="pricing">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Prix de base</Label>
              <NumericInput 
                value={product.base_price} 
                onChange={(v) => setProduct(prev => ({ ...prev, base_price: v }))}
                suffix="€"
              />
            </div>
            <div className="space-y-2">
              <Label>Investissement minimum</Label>
              <NumericInput 
                value={product.min_investment} 
                onChange={(v) => setProduct(prev => ({ ...prev, min_investment: v }))}
                suffix="€"
              />
            </div>
            <div className="space-y-2">
              <Label>Investissement maximum</Label>
              <NumericInput 
                value={product.max_investment} 
                onChange={(v) => setProduct(prev => ({ ...prev, max_investment: v }))}
                suffix="€"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox 
              checked={product.variable_price}
              onCheckedChange={(v) => setProduct(prev => ({ ...prev, variable_price: v }))}
            />
            <Label>Prix variable</Label>
          </div>

          <Collapsible>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm text-blue-500 hover:text-blue-400">
              <ChevronRight className="w-4 h-4" />
              Gestion des variations
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <p className="text-sm text-muted-foreground">
                Configuration des variations de prix à venir...
              </p>
            </CollapsibleContent>
          </Collapsible>

          <Collapsible open={tiersOpen} onOpenChange={setTiersOpen}>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm text-blue-500 hover:text-blue-400">
              <ChevronRight className={`w-4 h-4 transition-transform ${tiersOpen ? 'rotate-90' : ''}`} />
              Grille de taux par montant ({product.percentage_by_amount?.length || 0} palier{(product.percentage_by_amount?.length || 0) > 1 ? 's' : ''})
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4 space-y-3">
              <p className="text-xs text-muted-foreground">
                Definissez des taux differents selon le montant investi. Si vide, le taux principal s'applique a tous.
              </p>
              {(product.percentage_by_amount || []).map((tier, idx) => (
                <div key={idx} className="grid grid-cols-4 gap-2 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs">Min (€)</Label>
                    <NumericInput
                      value={tier.min_amount}
                      onChange={(v) => {
                        const updated = [...product.percentage_by_amount];
                        updated[idx] = { ...updated[idx], min_amount: v };
                        setProduct(prev => ({ ...prev, percentage_by_amount: updated }));
                      }}
                      suffix="€"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Max (€)</Label>
                    <NumericInput
                      value={tier.max_amount}
                      onChange={(v) => {
                        const updated = [...product.percentage_by_amount];
                        updated[idx] = { ...updated[idx], max_amount: v };
                        setProduct(prev => ({ ...prev, percentage_by_amount: updated }));
                      }}
                      suffix="€"
                      placeholder="Illimite"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Taux (%)</Label>
                    <NumericInput
                      value={tier.percentage}
                      onChange={(v) => {
                        const updated = [...product.percentage_by_amount];
                        updated[idx] = { ...updated[idx], percentage: v };
                        setProduct(prev => ({ ...prev, percentage_by_amount: updated }));
                      }}
                      suffix="%"
                    />
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 text-red-500 hover:text-red-600"
                    onClick={() => {
                      const updated = product.percentage_by_amount.filter((_, i) => i !== idx);
                      setProduct(prev => ({ ...prev, percentage_by_amount: updated }));
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const tiers = product.percentage_by_amount || [];
                  const lastMax = tiers.length > 0 ? (tiers[tiers.length - 1].max_amount || 0) : 0;
                  setProduct(prev => ({
                    ...prev,
                    percentage_by_amount: [...(prev.percentage_by_amount || []), { min_amount: lastMax > 0 ? lastMax + 1 : 0, max_amount: 0, percentage: 0 }]
                  }));
                }}
                data-testid="add-tier-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter un palier
              </Button>
            </CollapsibleContent>
          </Collapsible>
        </SectionHeader>

        {/* Gestion du produit */}
        <SectionHeader icon={CalendarIcon} title="Gestion du produit" section="product">
          <div className="flex items-center gap-2 pb-4">
            <Checkbox 
              checked={product.show_at_launch}
              onCheckedChange={(v) => setProduct(prev => ({ ...prev, show_at_launch: v }))}
            />
            <Label>Afficher au lancement du produit</Label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Début de disponibilité</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {product.launch_date 
                      ? format(new Date(product.launch_date), 'dd/MM/yyyy', { locale: fr })
                      : 'Sélectionner...'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                  <Calendar
                    mode="single"
                    selected={product.launch_date ? new Date(product.launch_date) : undefined}
                    onSelect={(date) => setProduct(prev => ({ 
                      ...prev, 
                      launch_date: date ? format(date, 'yyyy-MM-dd') : '' 
                    }))}
                    locale={fr}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Fin de disponibilité</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {product.end_date 
                      ? format(new Date(product.end_date), 'dd/MM/yyyy', { locale: fr })
                      : 'Sélectionner...'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                  <Calendar
                    mode="single"
                    selected={product.end_date ? new Date(product.end_date) : undefined}
                    onSelect={(date) => setProduct(prev => ({ 
                      ...prev, 
                      end_date: date ? format(date, 'yyyy-MM-dd') : '' 
                    }))}
                    locale={fr}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={product.enable_offers}
                onCheckedChange={(v) => setProduct(prev => ({ ...prev, enable_offers: v }))}
              />
              <Label>Activer les propositions d'offres sur le produit</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={product.show_reference}
                onCheckedChange={(v) => setProduct(prev => ({ ...prev, show_reference: v }))}
              />
              <Label>Afficher la référence</Label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={product.is_watch}
                onCheckedChange={(v) => setProduct(prev => ({ ...prev, is_watch: v }))}
              />
              <Label>Ce produit est une montre</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={product.is_savings}
                onCheckedChange={(v) => setProduct(prev => ({ ...prev, is_savings: v }))}
              />
              <Label>Ce produit est une épargne</Label>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox 
              checked={product.linked_to_positions}
              onCheckedChange={(v) => setProduct(prev => ({ ...prev, linked_to_positions: v }))}
            />
            <Label>Le produit sera lié à des positions (trade)</Label>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox 
              checked={product.funds_locked}
              onCheckedChange={(v) => setProduct(prev => ({ ...prev, funds_locked: v }))}
              data-testid="funds-locked-checkbox"
            />
            <Label>Fonds bloqués durant la durée du contrat</Label>
          </div>
        </SectionHeader>

        {/* Gestion des images */}
        <SectionHeader icon={ImageIcon} title="Gestion des images" section="images">
          <div className="grid grid-cols-4 gap-4">
            {product.images.map((img, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square rounded-lg border-2 border-dashed border-border/50 bg-muted/20 overflow-hidden">
                  {img ? (
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                  )}
                </div>
                <Button
                  size="icon"
                  variant="destructive"
                  className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeImage(index)}
                >
                  <X className="w-3 h-3" />
                </Button>
                <div className="mt-2 space-y-1">
                  <Input
                    value={img}
                    onChange={(e) => updateImage(index, e.target.value)}
                    placeholder="URL de l'image..."
                    className="text-xs"
                  />
                  <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded bg-primary/10 hover:bg-primary/20 text-primary text-xs transition-colors w-full justify-center" data-testid={`upload-image-btn-${index}`}>
                    <Upload className="w-3 h-3" />
                    {uploadingImage === index ? 'Upload...' : 'Uploader'}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, index)} disabled={uploadingImage === index} />
                  </label>
                </div>
              </div>
            ))}
            <div className="space-y-2">
              <button
                onClick={addImage}
                className="aspect-square rounded-lg border-2 border-dashed border-border/50 bg-muted/20 hover:bg-muted/30 transition-colors flex flex-col items-center justify-center gap-2 w-full"
              >
                <Plus className="w-8 h-8 text-muted-foreground/50" />
                <span className="text-xs text-muted-foreground">URL</span>
              </button>
              <label className="cursor-pointer rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors flex flex-col items-center justify-center gap-2 p-4 w-full" data-testid="upload-new-image-btn">
                <Upload className="w-6 h-6 text-primary/60" />
                <span className="text-xs text-primary/80">{uploadingImage === -1 ? 'Upload...' : 'Uploader une image'}</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleNewImageUpload} disabled={uploadingImage === -1} />
              </label>
            </div>
          </div>
        </SectionHeader>

        {/* Gestion du contrat */}
        <SectionHeader icon={Shield} title="Gestion du contrat" section="contract">
          {/* Contract Version Selector */}
          <div className="space-y-3 p-4 rounded-lg border border-border/50 bg-muted/10">
            <Label className="font-semibold">Version du contrat PDF</Label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setProduct(prev => ({ ...prev, contract_version: 'v1' }))}
                className={`p-3 rounded-lg border-2 text-left transition-all ${!product.contract_version || product.contract_version === 'v1' ? 'border-primary bg-primary/10' : 'border-border/50 hover:border-border'}`}
              >
                <p className="font-semibold text-sm">V1 — Classic</p>
                <p className="text-xs text-muted-foreground mt-1">Contrat standard, 3 sections, recapitulatif et signatures</p>
              </button>
              <button
                type="button"
                onClick={() => setProduct(prev => ({ ...prev, contract_version: 'v2' }))}
                className={`p-3 rounded-lg border-2 text-left transition-all ${product.contract_version === 'v2' ? 'border-primary bg-primary/10' : 'border-border/50 hover:border-border'}`}
              >
                <p className="font-semibold text-sm">V2 — Premium</p>
                <p className="text-xs text-muted-foreground mt-1">15 articles detailles, grille tarifaire, RGPD, garanties</p>
              </button>
              <button
                type="button"
                onClick={() => setProduct(prev => ({ ...prev, contract_version: 'custom' }))}
                className={`p-3 rounded-lg border-2 text-left transition-all ${product.contract_version === 'custom' ? 'border-primary bg-primary/10' : 'border-border/50 hover:border-border'}`}
              >
                <p className="font-semibold text-sm">Personnalise</p>
                <p className="text-xs text-muted-foreground mt-1">Redigez vos propres articles, le systeme ajoute le reste</p>
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {(!product.contract_version || product.contract_version === 'v1') && "Le contrat Classic genere automatiquement les modalites de base avec le recapitulatif et les signatures."}
              {product.contract_version === 'v2' && "Le contrat Premium genere 15 articles professionnels adaptes au produit (taux, duree, fonds bloques, interets composes, etc.), avec echeancier, recapitulatif et signatures."}
              {product.contract_version === 'custom' && "Redigez uniquement vos articles et modalites ci-dessous. Le systeme ajoute automatiquement : infos client, infos plateforme, echeancier, recapitulatif et signatures."}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              data-testid="preview-contract-btn"
              onClick={async () => {
                const ver = product.contract_version || 'v1';
                try {
                  toast.info('Generation de l\'apercu...');
                  const res = await axios.post(`${API}/placements/contract/preview-version`, {
                    product_id: productId || null,
                    contract_version: ver,
                    terms_conditions: ver === 'custom' ? product.terms_conditions : null,
                    product_data: {
                      name: product.name || 'Produit',
                      duration_months: product.duration_months,
                      rentability_rate: product.rentability_rate,
                      rentability_period: product.rentability_period,
                      interest_compound: product.interest_compound,
                      interest_payout_target: product.interest_payout_target,
                      funds_locked: product.funds_locked,
                      percentage_by_amount: product.percentage_by_amount,
                      min_investment: product.min_investment,
                      max_investment: product.max_investment,
                      auto_renewal: product.auto_renewal,
                    }
                  }, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  window.open(url, '_blank');
                } catch (err) {
                  toast.error('Erreur apercu', { description: err.response?.data?.detail || 'Impossible de generer' });
                }
              }}
            >
              <Eye className="w-4 h-4 mr-1" />
              Apercu PDF ({(product.contract_version || 'v1').toUpperCase()})
            </Button>
          </div>

          {/* Custom contract textarea - only shown for 'custom' */}
          {product.contract_version === 'custom' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Vos articles et modalites</Label>
                  <p className="text-xs text-muted-foreground">Ecrivez uniquement les articles du contrat. Ne mettez PAS les infos client, societe ou signatures — le systeme les ajoute automatiquement au PDF.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const name = product.name || 'Produit';
                    const defaultContract = `ARTICLE 1 – DÉFINITION\n\nLe Compte Épargne ${name} est conçu pour offrir au titulaire la possibilité de placer des fonds pour une durée déterminée.\n\nARTICLE 2 – CONDITIONS D'OUVERTURE\n\nAccessible à toute personne physique majeure résidant en Europe.\n\nARTICLE 3 – MODALITÉS\n\nLes intérêts sont versés selon la période choisie.\n\nARTICLE 4 – FISCALITÉ\n\nLes taux mentionnés sont nets d'impôt.\n\nARTICLE 5 – CLÔTURE\n\nLe capital et les intérêts sont restitués à l'échéance.`;
                    setProduct(prev => ({ ...prev, terms_conditions: defaultContract }));
                    toast.success('Modele charge — personnalisez les articles');
                  }}
                  data-testid="load-default-contract-btn"
                >
                  Charger un modele
                </Button>
              </div>
              <textarea 
                defaultValue={product.terms_conditions || ''}
                onBlur={(e) => setProduct(prev => ({ ...prev, terms_conditions: e.target.value }))}
                rows={20}
                placeholder={"ARTICLE 1 – DÉFINITION\n\nVotre texte ici...\n\nARTICLE 2 – CONDITIONS\n\nVotre texte ici..."}
                className="w-full font-mono text-xs leading-relaxed rounded-md border border-input bg-background px-3 py-2 outline-none focus:ring-1 focus:ring-ring"
                data-testid="product-contract-textarea"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>URL de la plaquette (PDF)</Label>
            <Input 
              value={product.brochure_url || ''} 
              onChange={(e) => setProduct(prev => ({ ...prev, brochure_url: e.target.value }))}
              placeholder="https://..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={product.auto_renewal}
                onCheckedChange={(v) => setProduct(prev => ({ ...prev, auto_renewal: v }))}
              />
              <Label>Reconduction automatique</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={product.contract_guarantee}
                onCheckedChange={(v) => setProduct(prev => ({ ...prev, contract_guarantee: v }))}
              />
              <Label>Contrat garantie</Label>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Indice de confiance (0-100)</Label>
            <div className="flex items-center gap-4">
              <Input 
                type="range"
                min="0"
                max="100"
                value={product.confidence_index} 
                onChange={(e) => setProduct(prev => ({ ...prev, confidence_index: parseInt(e.target.value) }))}
                className="flex-1"
              />
              <span className="w-12 text-center font-bold">{product.confidence_index}%</span>
            </div>
          </div>
        </SectionHeader>

        {/* Options */}
        <SectionHeader icon={Settings} title="Options" section="options">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={product.is_crowdfunding}
                onCheckedChange={(v) => setProduct(prev => ({ ...prev, is_crowdfunding: v }))}
              />
              <Label>Activer crowdfunding</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={product.manage_stock}
                onCheckedChange={(v) => setProduct(prev => ({ ...prev, manage_stock: v }))}
              />
              <Label>Activer la gestion des stocks</Label>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox 
              checked={product.is_unique}
              onCheckedChange={(v) => setProduct(prev => ({ ...prev, is_unique: v }))}
            />
            <Label>Produit unique (limite la quantité à 1 par client)</Label>
          </div>
        </SectionHeader>

        {/* Bannière */}
        <SectionHeader icon={Flag} title="Bannière" section="banner">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Pays</Label>
              <Input 
                value={product.banner_country || ''} 
                onChange={(e) => setProduct(prev => ({ ...prev, banner_country: e.target.value }))}
                placeholder="FR, BE, CH..."
              />
            </div>
            <div className="space-y-2">
              <Label>Bannière (texte)</Label>
              <Input 
                value={product.banner_text || ''} 
                onChange={(e) => setProduct(prev => ({ ...prev, banner_text: e.target.value }))}
                placeholder="DISPONIBLE, ÉPUISÉ..."
              />
            </div>
            <div className="space-y-2">
              <Label>Couleur de la bannière</Label>
              <div className="flex gap-2">
                <Input 
                  type="color"
                  value={product.banner_color || '#22c55e'} 
                  onChange={(e) => setProduct(prev => ({ ...prev, banner_color: e.target.value }))}
                  className="w-14 h-10 p-1"
                />
                <Input 
                  value={product.banner_color || '#22c55e'} 
                  onChange={(e) => setProduct(prev => ({ ...prev, banner_color: e.target.value }))}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          
          {product.banner_text && (
            <div className="mt-4">
              <Label className="mb-2 block">Aperçu</Label>
              <div 
                className="inline-block px-3 py-1 rounded text-white text-sm font-medium"
                style={{ backgroundColor: product.banner_color || '#22c55e' }}
              >
                {product.banner_text}
              </div>
            </div>
          )}
        </SectionHeader>

        {/* Save Button Bottom */}
        <div className="flex justify-end gap-4 pt-4 border-t border-border/50">
          <Button variant="outline" onClick={() => navigate('/admin/placements')}>
            Annuler
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Enregistrement...' : 'Enregistrer le produit'}
          </Button>
        </div>
      </div>

      {/* Guide de paramétrage */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="placement-guide-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <HelpCircle className="w-5 h-5 text-blue-500" />
              Guide de paramétrage d&apos;un livret
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4 text-sm">

            <div className="p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
              <h3 className="font-bold text-blue-400 mb-2">RENTABILITÉ</h3>
              <div className="space-y-3">
                <div>
                  <p className="font-semibold">Taux de rentabilité</p>
                  <p className="text-muted-foreground">Le pourcentage d&apos;intérêt appliqué au capital. Ex: 0.49% mensuel = le client touche 0.49% de son capital chaque mois.</p>
                </div>
                <div>
                  <p className="font-semibold">Taux variable</p>
                  <p className="text-muted-foreground">Si renseigné, le contrat affiche &quot;de X% à Y%&quot;. Le taux réel appliqué reste le taux principal.</p>
                </div>
                <div>
                  <p className="font-semibold">Période de rentabilité</p>
                  <p className="text-muted-foreground">Détermine sur quelle base le taux est exprimé : <b>Mensuel</b> (0.49%/mois), <b>Annuel</b> (4.9%/an), <b>Trimestriel</b>, etc. Ce n&apos;est PAS la fréquence de versement.</p>
                </div>
                <div>
                  <p className="font-semibold">Grille de taux par montant (percentage_by_amount)</p>
                  <p className="text-muted-foreground">Permet de définir des taux différents selon le montant investi. Ex: 5 000€-9 999€ = 5.90%, 10 000€-24 999€ = 6.90%. Si vide, le taux principal s&apos;applique à tous.</p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg border bg-emerald-500/5 border-emerald-500/20">
              <h3 className="font-bold text-emerald-400 mb-2">INTÉRÊTS &amp; VERSEMENTS</h3>
              <div className="space-y-3">
                <div>
                  <p className="font-semibold">Périodes de versement autorisées</p>
                  <p className="text-muted-foreground">Cochez les fréquences de versement disponibles pour ce produit : Mensuel, Trimestriel, Semestriel, Annuel, Fin de contrat. Le client choisit parmi celles activées lors de la souscription.</p>
                </div>
                <div className="p-3 rounded bg-card border border-border/50">
                  <p className="font-semibold">Intérêts composés (cumulables)</p>
                  <p className="text-muted-foreground">Si <b>coché</b> : les intérêts de chaque période sont ajoutés au capital, et les intérêts suivants sont calculés sur le nouveau capital (effet boule de neige).</p>
                  <p className="text-muted-foreground mt-1">Si <b>décoché</b> : intérêts simples — toujours calculés sur le capital initial uniquement.</p>
                  <p className="text-xs text-amber-400 mt-2">N&apos;a d&apos;effet que si le versement est sur &quot;Capital du livret&quot;. Si les intérêts vont dans &quot;Fonds disponibles&quot;, il n&apos;y a rien à cumuler.</p>
                </div>
                <div className="p-3 rounded bg-card border border-border/50">
                  <p className="font-semibold">Versement des intérêts</p>
                  <p className="text-muted-foreground"><b>Fonds disponibles</b> : les intérêts sont versés dans le portefeuille du client. Il peut les retirer. Le capital du livret ne change pas.</p>
                  <p className="text-muted-foreground mt-1"><b>Capital du livret</b> : les intérêts s&apos;ajoutent au capital du livret. Exemple : 10 000€ de capital + 500€ d&apos;intérêts = livret passe à 10 500€.</p>
                </div>
                <div className="p-3 rounded bg-card border border-border/50">
                  <p className="font-semibold">Capitalisation des fonds</p>
                  <p className="text-muted-foreground">Si <b>coché</b> : les fonds sont <b>bloqués</b> pendant toute la durée du contrat. Le client ne peut pas retirer avant l&apos;échéance.</p>
                  <p className="text-muted-foreground mt-1">Si <b>décoché</b> : les fonds restent disponibles à tout moment.</p>
                  <p className="text-xs text-amber-400 mt-2">C&apos;est différent des intérêts composés. La capitalisation concerne le blocage des fonds, pas le mode de calcul.</p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg border bg-purple-500/5 border-purple-500/20">
              <h3 className="font-bold text-purple-400 mb-2">EXEMPLES CONCRETS</h3>
              <div className="space-y-2 text-muted-foreground">
                <p><b>Livret classique</b> : Taux 0.49% mensuel, versement mensuel, fonds disponibles, pas d&apos;intérêts composés → le client touche 49€/mois sur 10 000€, peut retirer quand il veut.</p>
                <p><b>Épargne bloquée</b> : Taux 9.75% mensuel, versement mensuel, capital du livret, intérêts composés, capitalisation activée → rendement exponentiel, fonds bloqués jusqu&apos;à l&apos;échéance.</p>
                <p><b>Placement annuel</b> : Taux 7.5% annuel, versement annuel, fonds disponibles → le client touche une fois par an.</p>
              </div>
            </div>

            <div className="p-4 rounded-lg border bg-amber-500/5 border-amber-500/20">
              <h3 className="font-bold text-amber-400 mb-2">CONTRAT PDF — COMMENT ÇA MARCHE</h3>
              <div className="space-y-3">
                <div className="p-3 rounded bg-card border border-border/50">
                  <p className="font-semibold">Le contrat par défaut (automatique)</p>
                  <p className="text-muted-foreground">Si vous laissez le champ &quot;Contrat&quot; <b>vide</b>, le système génère automatiquement un contrat PDF complet avec 10 articles juridiques standard (Définition, Conditions d&apos;ouverture, Modalités, Fiscalité, Clôture, Anti-blanchiment, Données personnelles, Réclamation, Garantie des dépôts, Loi applicable). Le nom de votre plateforme, l&apos;adresse, les taux et les infos du client sont injectés automatiquement.</p>
                </div>
                <div className="p-3 rounded bg-card border border-border/50">
                  <p className="font-semibold">Personnaliser le contrat d&apos;un produit</p>
                  <p className="text-muted-foreground">Cliquez <b>&quot;Charger le contrat par défaut&quot;</b> pour pré-remplir le champ avec les 10 articles. Vous pouvez ensuite <b>modifier, ajouter, supprimer</b> n&apos;importe quel article. Ce texte remplacera le contrat automatique dans le PDF pour ce produit.</p>
                  <p className="text-muted-foreground mt-1">Séparez chaque article par une ligne vide. Les lignes commençant par &quot;ARTICLE&quot; seront automatiquement mises en gras dans le PDF.</p>
                </div>
                <div className="p-3 rounded bg-card border border-border/50">
                  <p className="font-semibold">Personnaliser le contrat d&apos;un client spécifique</p>
                  <p className="text-muted-foreground">Chaque souscription peut avoir son propre contrat. Allez sur la <b>fiche du client → onglet Transactions → Souscriptions Placements → Éditer</b>. Vous y trouverez un champ &quot;Contrat - Conditions Générales&quot; pré-rempli avec le contrat du produit. Modifiez-le pour ce client uniquement.</p>
                </div>
                <div className="p-3 rounded bg-card border border-border/50">
                  <p className="font-semibold">Priorité du contrat dans le PDF</p>
                  <p className="text-muted-foreground">Le PDF utilise le texte dans cet ordre :</p>
                  <ol className="text-muted-foreground list-decimal ml-4 mt-1 space-y-1">
                    <li><b>Contrat personnalisé du client</b> (si renseigné dans la souscription)</li>
                    <li><b>Contrat du produit</b> (si renseigné dans ce formulaire)</li>
                    <li><b>Contrat automatique</b> (les 10 articles standard générés par le système)</li>
                  </ol>
                </div>
                <div className="p-3 rounded bg-card border border-border/50">
                  <p className="font-semibold">Ce qui est toujours généré automatiquement (non modifiable)</p>
                  <p className="text-muted-foreground">Même si vous personnalisez les articles, le PDF génère toujours automatiquement :</p>
                  <ul className="text-muted-foreground list-disc ml-4 mt-1 space-y-1">
                    <li>Page de couverture avec le nom du produit</li>
                    <li>Informations de la société et de l&apos;investisseur</li>
                    <li>Articles 1 (Objet), 2 (Durée), 3 (Paiement) avec les vraies données</li>
                    <li>Récapitulatif (titre, durée, rentabilité, montant)</li>
                    <li>Signatures (vendeur + acquéreur)</li>
                    <li>Échéancier des intérêts (tableau avec dates et montants)</li>
                    <li>En-tête et pied de page (logo, adresse, numéro de page)</li>
                  </ul>
                  <p className="text-muted-foreground mt-1">Votre texte personnalisé remplace uniquement la section &quot;Termes &amp; Conditions&quot; (les articles détaillés à la fin du PDF).</p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg border bg-muted/30">
              <h3 className="font-bold mb-2">AUTRES OPTIONS</h3>
              <div className="space-y-2 text-muted-foreground">
                <p><b>Reconduction automatique</b> : le contrat se renouvelle automatiquement à l&apos;échéance.</p>
                <p><b>Contrat garantie</b> : affiche un badge &quot;Garantie&quot; sur la fiche produit côté client.</p>
                <p><b>Indice de confiance</b> : score de 0 à 100 affiché au client pour indiquer la fiabilité du produit.</p>
                <p><b>À la une</b> : met le produit en avant sur le dashboard client.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowGuide(false)}>Compris</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
